import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Single blog post page. Renders markdown with `react-markdown` + GFM.
 * Custom renderers ensure the body stays brand-on (font-display headings,
 * navy body copy, gold accent links).
 */
export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setStatus("loading");
      try {
        const { data } = await axios.get(`${API}/blog/posts/${slug}`);
        if (!cancelled) {
          setPost(data);
          setStatus("ok");
        }
      } catch (e) {
        if (cancelled) return;
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[var(--bone)]" data-testid="blog-post-loading">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-[var(--muted)]">Loading…</div>
      </main>
    );
  }

  if (status !== "ok" || !post) {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[var(--bone)]" data-testid="blog-post-notfound">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="eyebrow">Not Found</div>
          <h1 className="font-display text-[36px] md:text-[44px] text-[var(--navy-900)] mt-4">
            This Post Has Moved On.
          </h1>
          <p className="mt-5 text-[16px] leading-[1.7] text-[var(--muted)] max-w-xl">
            We couldn't find that piece. It may have been retired or the link may have changed.
          </p>
          <Link
            to="/blog"
            className="btn-primary mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[12px] tracking-[0.24em] uppercase font-bold"
          >
            <ArrowLeft size={14} /> Back To All Posts
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="page-blog-post" className="pt-24 bg-[var(--bone)] min-h-screen">
      {/* HERO */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-20 lg:py-24 relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-50"></div>
        {post.hero_image_url && (
          <>
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${post.hero_image_url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(115deg, rgba(5,13,28,0.85) 0%, rgba(5,13,28,0.65) 55%, rgba(5,13,28,0.40) 100%)",
              }}
            />
          </>
        )}
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-bold"
            style={{ color: "var(--gold-soft)" }}
          >
            <ArrowLeft size={12} /> All Posts
          </Link>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span
              className="text-[9px] tracking-[0.32em] uppercase font-bold"
              style={{ color: post.format === "essay" ? "var(--gold-soft)" : "var(--accent-gold-soft)" }}
            >
              {post.format === "essay" ? "Essay" : "Field Note"}
            </span>
            <span className="text-[11px] tracking-[0.16em] uppercase text-[var(--navy-100)]/70">
              {formatDate(post.published_at || post.created_at)}
            </span>
          </div>
          <h1 className="font-display text-[36px] md:text-[56px] leading-[1.05] mt-5">{post.title}</h1>
          {post.subtitle && (
            <p className="mt-6 text-[18px] leading-[1.6] text-[var(--bone)]/85 max-w-2xl">{post.subtitle}</p>
          )}
        </div>
      </section>

      {/* BODY */}
      <section className="py-16 lg:py-24">
        <article
          data-testid="blog-post-body"
          className="max-w-3xl mx-auto px-6 lg:px-10 prose prose-fim"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ ...props }) => (
                <h1 className="font-display text-[34px] md:text-[42px] leading-[1.1] text-[var(--navy-900)] mt-12 mb-6" {...props} />
              ),
              h2: ({ ...props }) => (
                <h2 className="font-display text-[26px] md:text-[32px] leading-[1.15] text-[var(--navy-900)] mt-10 mb-5" {...props} />
              ),
              h3: ({ ...props }) => (
                <h3 className="font-display text-[20px] md:text-[24px] leading-[1.2] text-[var(--navy-900)] mt-8 mb-4" {...props} />
              ),
              p: ({ ...props }) => (
                <p className="text-[17px] md:text-[18px] leading-[1.75] text-[var(--navy-900)] mb-6" {...props} />
              ),
              a: ({ ...props }) => (
                <a
                  className="underline underline-offset-2 font-semibold"
                  style={{ color: "var(--gold)" }}
                  target={props.href?.startsWith("http") ? "_blank" : undefined}
                  rel={props.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                  {...props}
                />
              ),
              ul: ({ ...props }) => <ul className="list-disc pl-6 space-y-3 mb-6 text-[17px] text-[var(--navy-900)]" {...props} />,
              ol: ({ ...props }) => <ol className="list-decimal pl-6 space-y-3 mb-6 text-[17px] text-[var(--navy-900)]" {...props} />,
              blockquote: ({ ...props }) => (
                <blockquote
                  className="border-l-[3px] pl-5 my-8 italic text-[18px] leading-[1.7] text-[var(--navy-900)]"
                  style={{ borderLeftColor: "var(--accent-gold)" }}
                  {...props}
                />
              ),
              code: ({ inline, ...props }) =>
                inline ? (
                  <code className="bg-[var(--navy-50)] px-1.5 py-0.5 text-[14px] rounded" {...props} />
                ) : (
                  <code className="block bg-[var(--navy-950)] text-[var(--bone)] p-5 my-6 text-[13px] leading-[1.65] overflow-x-auto" {...props} />
                ),
              hr: () => <hr className="my-12 border-t border-[rgba(10,31,68,0.15)]" />,
            }}
          >
            {post.body || ""}
          </ReactMarkdown>

          {post.tags?.length > 0 && (
            <div className="mt-14 pt-8 border-t border-[rgba(10,31,68,0.10)] flex flex-wrap gap-1.5">
              {post.tags.map((t) => (
                <span
                  key={t}
                  className="text-[9px] tracking-[0.22em] uppercase text-[var(--muted)] px-2.5 py-1 border border-[rgba(10,31,68,0.10)] rounded"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </article>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="eyebrow-light" style={{ color: "var(--gold-soft)" }}>
            Continue
          </div>
          <h2 className="font-display text-[28px] md:text-[36px] leading-[1.1] mt-4">
            More Notes From The Work.
          </h2>
          <Link
            to="/blog"
            data-testid="blog-post-more"
            className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] tracking-[0.24em] uppercase font-bold"
            style={{ background: "var(--gold)", color: "var(--bone)" }}
          >
            All Posts <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}
